








































































import { Component, Vue } from 'vue-property-decorator'
import ApiClient from '@/services/api-client'
import { Account } from '@/models'

@Component
export default class MergeAccountsModal extends Vue {
  private selected: Account[] = []
  private masterId = ''
  private searchModel: Account | null = null
  private searchResults: Account[] = []
  private searchTimeout: any = null
  private merging = false

  private get selectedFields() {
    return [
      { key: 'master', label: this.$t('merge_accounts.master_col'), class: 'text-center align-middle', thStyle: { width: '90px' } },
      { key: 'account', label: this.$t('merge_accounts.account_col') },
      { key: 'ldap', label: this.$t('merge_accounts.ldap_col'), class: 'text-center align-middle' },
      { key: 'remove', label: '', class: 'text-center align-middle', thStyle: { width: '50px' } }
    ]
  }

  private get canMerge(): boolean {
    return this.selected.length >= 2 &&
      !!this.masterId &&
      this.selected.some(a => a.accountId === this.masterId)
  }

  onSearch(search: string, loading: (value: boolean) => void) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    if (!search || search.length < 2) {
      this.searchResults = []
      return
    }
    loading(true)
    this.searchTimeout = setTimeout(async() => {
      try {
        const result = await ApiClient.searchAccounts(search, 10, 0)
        const selectedIds = this.selected.map(a => a.accountId)
        this.searchResults = result.items.filter(a => !selectedIds.includes(a.accountId))
      } catch (error) {
        console.error('Error searching accounts:', error)
        this.searchResults = []
      } finally {
        loading(false)
      }
    }, 400)
  }

  addAccount(account: Account | null) {
    if (account && !this.selected.some(a => a.accountId === account.accountId)) {
      this.selected.push(account)
      this.updateDefaultMaster()
    }
    // reset the search box so the next pick starts clean
    this.$nextTick(() => {
      this.searchModel = null
      this.searchResults = []
    })
  }

  removeAccount(account: Account) {
    this.selected = this.selected.filter(a => a.accountId !== account.accountId)
    this.updateDefaultMaster()
  }

  // Default master = the LDAP-synced account if any, otherwise the first added.
  private updateDefaultMaster() {
    if (this.masterId && this.selected.some(a => a.accountId === this.masterId)) {
      return
    }
    const ldapAccount = this.selected.find(a => a.syncWithLdap)
    this.masterId = (ldapAccount || this.selected[0])?.accountId || ''
  }

  async confirmMerge() {
    if (!this.canMerge) return

    const alternateIds = this.selected
      .filter(a => a.accountId !== this.masterId)
      .map(a => a.accountId)

    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('merge_accounts.confirm_body', { master: this.masterId, count: alternateIds.length }).toString(),
      {
        title: this.$t('merge_accounts.confirm_title').toString(),
        size: 'md',
        okVariant: 'danger',
        okTitle: this.$t('merge_accounts.confirm').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )
    if (!confirmed) return

    this.merging = true
    try {
      const master = await ApiClient.mergeAccounts(this.masterId, alternateIds)
      this.$bvToast.toast(this.$t('merge_accounts.success', { master: this.masterId }).toString(), {
        title: this.$t('feedback.feedback_message_success_title').toString(),
        variant: 'success',
        solid: true
      })
      this.$emit('merged', master)
      this.hide()
    } catch (error: any) {
      console.error('Error merging accounts:', error)
      this.$bvToast.toast(error.message || this.$t('merge_accounts.error').toString(), {
        title: this.$t('merge_accounts.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    } finally {
      this.merging = false
    }
  }

  hide() {
    this.$bvModal.hide('MergeAccountsModal')
  }

  reset() {
    this.selected = []
    this.masterId = ''
    this.searchModel = null
    this.searchResults = []
    this.merging = false
  }
}
